import { apiGet, apiPost, apiPatch, apiDelete } from "./client";
import type {
  Lead,
  LeadInput,
  PersonSearchFilters,
} from "@sales-ops-amplify/shared";
import { getAccessToken } from "@/stores/authStore";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const USER_LEADS = "/api/user/leads";

/** List user Leads, optionally filtered. */
export async function listLeads(params?: {
  accountId?: string;
  opportunityId?: string;
}): Promise<{ people: Lead[]; total?: number }> {
  return apiGet(USER_LEADS, {
    ...(params?.accountId ? { accountId: params.accountId } : {}),
    ...(params?.opportunityId ? { opportunityId: params.opportunityId } : {}),
  });
}

export async function getLead(id: string): Promise<Lead> {
  return apiGet(`${USER_LEADS}/${id}`);
}

export async function createLead(input: LeadInput): Promise<Lead> {
  const res = await apiPost<{ person: Lead }>(USER_LEADS, input);
  return res.person;
}

export async function updateLead(
  id: string,
  updates: Partial<LeadInput>,
): Promise<Lead> {
  const res = await apiPatch<{ person: Lead }>(`${USER_LEADS}/${id}`, updates);
  return res.person;
}

export async function deleteLead(
  id: string,
): Promise<{ message: string; id: string }> {
  return apiDelete(`${USER_LEADS}/${encodeURIComponent(id)}`);
}

/** Soft-delete Leads by id and/or all matching filters. */
export async function bulkDeleteLeads(body: {
  ids?: string[];
  filters?: PersonSearchFilters;
  allMatching?: boolean;
}): Promise<{ deleted: number }> {
  return apiPost(`${USER_LEADS}/bulk-delete`, body);
}

export async function restoreLead(
  id: string,
): Promise<{ message: string; id: string }> {
  return apiPost(`${USER_LEADS}/${encodeURIComponent(id)}/restore`, {});
}

export async function purgeLead(
  id: string,
): Promise<{ message: string; id: string }> {
  return apiPost(`${USER_LEADS}/${encodeURIComponent(id)}/purge`, {});
}

export async function purgeDeletedLeads(body?: {
  ids?: string[];
}): Promise<{ purged: number }> {
  return apiPost(`${USER_LEADS}/purge-deleted`, body ?? {});
}

/** Export user-scoped Leads as full-field CSV. */
export async function exportLeadsCsv(leadIds?: string[]): Promise<Blob> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${USER_LEADS}/export`, {
    method: "POST",
    headers,
    body: JSON.stringify(leadIds?.length ? { leadIds } : {}),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      typeof errBody === "object" && errBody && "error" in errBody
        ? String((errBody as { error: string }).error)
        : "Export failed",
    );
  }
  return res.blob();
}
